import { TenantCredentialId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ContentFull, ContentHeader } from "components/layout";
import { trpc } from "components/rpc/trpc";
import { ChoiceTiles } from "components/ui/choice-tiles";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import React from "react";
import { Link } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { useFeature } from "store/selectors/hooks";
import { WellSkyApiCredentialForm } from "./wellsky-api-credential-form";
import { WellSkyAuthMethod, wellSkyAuthMethodOptions } from "./wellsky-auth-method";
import { WellSkyWebCredentialForm } from "./wellsky-web-credential-form";

type FormState =
    | { kind: "closed" }
    | { kind: "connecting"; method: WellSkyAuthMethod | null }
    | { kind: "updating"; method: WellSkyAuthMethod };

const CLOSED: FormState = { kind: "closed" };

export const ManageIntegrationsWellSky: React.FC = () => {
    const isFeatureEnabled = useFeature("wellSkyIntegration");
    const isApiKeyEnabled = useFeature("wellSkyApiKey");
    const productName = useProductName();
    const trpcClient = trpc.useTRPC();

    const webQuery = useQuery(
        trpcClient.wellSkyPersonalCare.getCredential.queryOptions(undefined, {
            enabled: isFeatureEnabled,
        }),
    );
    const apiQuery = useQuery(
        trpcClient.wellSkyConnect.getCredential.queryOptions(undefined, {
            enabled: isFeatureEnabled && isApiKeyEnabled,
        }),
    );

    const [form, setForm] = React.useState<FormState>(CLOSED);
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

    const clearMessages = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(null);
    }, []);

    const showError = React.useCallback((message: string) => {
        setSuccessMessage(null);
        setErrorMessage(message);
    }, []);

    const webCredential = webQuery.data;
    const apiCredential = isApiKeyEnabled ? apiQuery.data : null;
    const refetchWeb = webQuery.refetch;
    const refetchApi = apiQuery.refetch;

    const webTestMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.testConnection.mutationOptions(),
        onSuccess: result => {
            setErrorMessage(null);
            setSuccessMessage(`Connected to ${result.accountName} as ${result.username}`);
        },
        onError: error => showError(error.message || "Could not connect to WellSky"),
    });

    const apiTestMutation = useMutation({
        ...trpcClient.wellSkyConnect.testConnection.mutationOptions(),
        onSuccess: result => {
            setErrorMessage(null);
            setSuccessMessage(
                `Connected to ${result.accountName} (agency ${result.agencyId}) as ${apiCredential?.username}`,
            );
            refetchApi();
        },
        onError: error => showError(error.message || "Could not connect to WellSky"),
    });

    const webDeleteMutation = useMutation({
        ...trpcClient.wellSkyPersonalCare.deleteCredential.mutationOptions(),
        onSuccess: () => {
            setErrorMessage(null);
            setSuccessMessage("WellSky credentials removed");
            refetchWeb();
        },
        onError: error => showError(error.message || "Failed to remove credentials"),
    });

    const apiDeleteMutation = useMutation({
        ...trpcClient.wellSkyConnect.deleteCredential.mutationOptions(),
        onSuccess: () => {
            setErrorMessage(null);
            setSuccessMessage("WellSky API credentials removed");
            refetchApi();
        },
        onError: error => showError(error.message || "Failed to remove credentials"),
    });

    const handleWebSaved = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage("Connected to WellSky and saved the credentials");
        setForm(CLOSED);
        refetchWeb();
    }, [refetchWeb]);

    const handleApiSaved = React.useCallback(() => {
        setErrorMessage(null);
        setSuccessMessage(
            "Connected to the WellSky Connect API and saved the credentials",
        );
        setForm(CLOSED);
        refetchApi();
    }, [refetchApi]);

    const startConnecting = React.useCallback(
        (method: WellSkyAuthMethod | null) => {
            clearMessages();
            setForm({ kind: "connecting", method });
        },
        [clearMessages],
    );

    const startUpdating = React.useCallback(
        (method: WellSkyAuthMethod) => {
            clearMessages();
            setForm({ kind: "updating", method });
        },
        [clearMessages],
    );

    const handleRemoveWeb = React.useCallback(
        async (id: TenantCredentialId) => {
            const confirmed = await fancyConfirm(
                "Remove WellSky credentials?",
                "The Send to WellSky Personal Care action will be unavailable until new credentials are added.",
                "Yes, remove",
                "Cancel",
            );
            if (confirmed) {
                webDeleteMutation.mutate({ id });
            }
        },
        [webDeleteMutation],
    );

    const handleRemoveApi = React.useCallback(
        async (id: TenantCredentialId) => {
            const confirmed = await fancyConfirm(
                "Remove WellSky API credentials?",
                `${productName} will no longer be able to reach the WellSky Connect API.`,
                "Yes, remove",
                "Cancel",
            );
            if (confirmed) {
                apiDeleteMutation.mutate({ id });
            }
        },
        [apiDeleteMutation, productName],
    );

    if (!isFeatureEnabled) {
        return (
            <ContentFull>
                <ContentHeader>WellSky Personal Care</ContentHeader>
                <InlineBanner type="info">
                    The WellSky Personal Care integration is not enabled for this account.
                </InlineBanner>
            </ContentFull>
        );
    }

    const isLoading = webQuery.isLoading || (isApiKeyEnabled && apiQuery.isLoading);
    const nothingConnected = !webCredential && !apiCredential;
    const formIsOpen = form.kind !== "closed";
    const editingMethod = form.kind === "updating" ? form.method : null;

    return (
        <ContentFull>
            <ContentHeader>WellSky Personal Care</ContentHeader>
            <div className="space-y-4">
                <div>
                    Connect your WellSky Personal Care account to send home care clients
                    to WellSky as new prospects.
                </div>

                <InlineBanner type="info">
                    Field requirements for sending a lead to WellSky can be configured in{" "}
                    <Link
                        to="/settings/agency/home-care-clients"
                        className={defaultLinkStyle}>
                        Home Care Client settings
                    </Link>
                    .
                </InlineBanner>

                {successMessage && (
                    <InlineBanner type="success">{successMessage}</InlineBanner>
                )}
                {errorMessage && <InlineBanner type="error">{errorMessage}</InlineBanner>}

                {isLoading ? (
                    <div className="flex items-center space-x-2">
                        <Spinner />
                        <span>Loading...</span>
                    </div>
                ) : (
                    <>
                        {webCredential && editingMethod !== "usernamePassword" ? (
                            <ConnectedCard
                                title={isApiKeyEnabled ? "Username and password" : null}
                                fields={[
                                    {
                                        label: "Account domain",
                                        value: webCredential.accountName,
                                    },
                                    {
                                        label: "Username",
                                        value: webCredential.username,
                                    },
                                ]}
                                testPending={webTestMutation.isPending}
                                onTest={() => {
                                    clearMessages();
                                    webTestMutation.mutate();
                                }}
                                onUpdate={() => startUpdating("usernamePassword")}
                                onRemove={() => handleRemoveWeb(webCredential.id)}
                            />
                        ) : null}

                        {apiCredential && editingMethod !== "apiKey" ? (
                            <ConnectedCard
                                title="API key"
                                fields={[
                                    {
                                        label: "Account domain",
                                        value:
                                            apiCredential.accountName ||
                                            "Not confirmed. Use Test connection.",
                                    },
                                    {
                                        label: "Agency ID",
                                        value: apiCredential.agencyId,
                                    },
                                    {
                                        label: "App name",
                                        value: apiCredential.appName,
                                    },
                                    {
                                        label: "Username",
                                        value:
                                            apiCredential.username ||
                                            "Not set. Use Update credentials to add one.",
                                    },
                                ]}
                                testPending={apiTestMutation.isPending}
                                onTest={() => {
                                    clearMessages();
                                    apiTestMutation.mutate();
                                }}
                                onUpdate={() => startUpdating("apiKey")}
                                onRemove={() => handleRemoveApi(apiCredential.id)}
                            />
                        ) : null}

                        {nothingConnected && !formIsOpen && (
                            <PrimaryButton
                                onClick={() =>
                                    startConnecting(
                                        isApiKeyEnabled ? null : "usernamePassword",
                                    )
                                }>
                                Connect WellSky
                            </PrimaryButton>
                        )}
                    </>
                )}

                {form.kind !== "closed" ? (
                    <div className="p-4 border border-gray-200 rounded space-y-4">
                        <h3 className="font-semibold text-lg">WellSky credentials</h3>
                        {form.kind === "connecting" && isApiKeyEnabled ? (
                            <ChoiceTiles
                                options={wellSkyAuthMethodOptions}
                                columns={2}
                                value={form.method}
                                onSelect={method =>
                                    setForm({ kind: "connecting", method })
                                }
                            />
                        ) : null}

                        {form.method === "usernamePassword" ? (
                            <WellSkyWebCredentialForm
                                initialUsername={webCredential?.username ?? ""}
                                initialAccountName={webCredential?.accountName ?? ""}
                                onSaved={handleWebSaved}
                                onCancel={() => setForm(CLOSED)}
                                onError={showError}
                            />
                        ) : null}

                        {form.method === "apiKey" ? (
                            <WellSkyApiCredentialForm
                                initialAgencyId={apiCredential?.agencyId ?? ""}
                                initialAppName={apiCredential?.appName ?? ""}
                                initialUsername={apiCredential?.username ?? ""}
                                onSaved={handleApiSaved}
                                onCancel={() => setForm(CLOSED)}
                                onError={showError}
                            />
                        ) : null}

                        {form.method === null ? (
                            <div className="flex justify-end">
                                <SecondaryButton onClick={() => setForm(CLOSED)}>
                                    Cancel
                                </SecondaryButton>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </ContentFull>
    );
};

interface ConnectedCardField {
    label: string;
    value: string;
}

interface ConnectedCardProps {
    title: string | null;
    fields: ConnectedCardField[];
    testPending: boolean;
    onTest: () => void;
    onUpdate: () => void;
    onRemove: () => void;
}

const ConnectedCard: React.FC<ConnectedCardProps> = props => (
    <div className="p-3 border border-gray-100 rounded space-y-2">
        {props.title ? (
            <div className="text-lg font-medium text-gray-900">{props.title}</div>
        ) : null}
        {props.fields.map(field => (
            <div key={field.label}>
                <div className="text-sm text-gray-500">{field.label}</div>
                <div className="font-medium">{field.value}</div>
            </div>
        ))}
        <div className="flex space-x-2 pt-2">
            <SecondaryButton onClick={props.onTest} disabled={props.testPending}>
                {props.testPending && <Spinner />}
                <span className={props.testPending ? "ml-2" : ""}>Test connection</span>
            </SecondaryButton>
            <SecondaryButton onClick={props.onUpdate}>Update credentials</SecondaryButton>
            <SecondaryButton onClick={props.onRemove}>Remove</SecondaryButton>
        </div>
    </div>
);
